import React, { useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { UserContext } from "../../auth/auth-context";
import { useSelectedBusiness } from "../../../redux/store";
import { UPDATE_BUSINESS_BASIC } from "../../../graphql/mutations";
import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../../graphql/queries";
import { normalizeBusinessData } from "../../../redux/reducers/loadReduxStoreData";
import { addBusiness } from "../../../redux/actions/businessActions";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import { FeatureAccess, getFeatureAccess } from "../../../util/featureAccess";
import {
  getBusinessProductScopes,
  isScopeVisible,
  ProductScope,
} from "../../../../core/business/productScopes";
import { getProductPlanTierCode, hasStandaloneBookingAccess } from "../../../../core/business/productAccess";

const ADMIN_ROLE = "ADMIN";
const NO_ACCESS_ROLE = "NO ACCESS";

const PERMISSION_GROUPS = [
  {
    title: "Staff",
    scope: ProductScope.SHARED,
    items: [
      "viewStaff",
      "addStaff",
      "editStaff",
      "deleteStaff",
      "viewStaff_own",
      "editStaff_own",
      "deleteStaff_own",
      "editStaff_image_own",
      "editStaff_integeration_own",
    ],
  },
  {
    title: "Business",
    scope: ProductScope.SHARED,
    items: ["viewBusiness", "addBusiness", "editBusiness", "deleteBusiness"],
  },
  {
    title: "Calendar",
    scope: ProductScope.BOOKING,
    items: [
      "viewCalendar",
      "addCalendar",
      "editCalendar",
      "deleteCalendar",
      "viewCalendar_own",
      "editCalendar_own",
      "deleteCalendar_own",
    ],
  },
  {
    title: "Booking Types",
    scope: ProductScope.BOOKING,
    items: [
      "viewBookingType",
      "addBookingType",
      "editBookingType",
      "deleteBookingType",
      "viewBookingType_own",
      "addBookingType_own",
      "editBookingType_own",
      "deleteBookingType_own",
    ],
  },
  {
    title: "Residents",
    scope: ProductScope.CARE,
    items: ["viewResident", "addResident", "editResident", "deleteResident"],
  },
  {
    title: "Jobs",
    scope: ProductScope.CARE,
    items: ["viewJob", "addJob", "editJob", "deleteJob"],
  },
];
const ALL_PERMISSION_KEYS = Array.from(
  new Set(PERMISSION_GROUPS.flatMap((group) => group.items))
);

const normalizeRoleName = (value) => String(value || "").trim().toUpperCase();

const toPermissionArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch (_err) {
      return [];
    }
  }
  return [];
};

const toRolePayload = (roles = []) =>
  roles.map((role) => ({
    id: role?.id ? Number(role.id) : undefined,
    name: String(role?.name || "").trim(),
    description: String(role?.description || "").trim(),
    permissions: toPermissionArray(role?.permissions),
  }));

const toEffectiveRolePermissions = (role, allPermissionKeys = ALL_PERMISSION_KEYS) => {
  const raw = toPermissionArray(role?.permissions);
  if (normalizeRoleName(role?.name) === ADMIN_ROLE) {
    return Array.from(new Set([...raw, ...allPermissionKeys]));
  }
  return raw;
};

export default function BusinessRoleBuilderTab({ isDisabled = false }) {
  const dispatch = useDispatch();
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const businessBasic = selectedBusiness?.businessBasic;
  const businessId = Number(businessBasic?.id || 0);
  const businessStaffs = selectedBusiness?.businessStaffs || [];
  const activeScopes = useMemo(
    () => getBusinessProductScopes(providerUser, selectedBusiness),
    [providerUser, selectedBusiness]
  );
  const visiblePermissionGroups = useMemo(
    () =>
      PERMISSION_GROUPS.filter((group) =>
        isScopeVisible(group.scope, activeScopes)
      ),
    [activeScopes]
  );
  const visiblePermissionKeys = useMemo(
    () =>
      Array.from(
        new Set(visiblePermissionGroups.flatMap((group) => group.items))
      ),
    [visiblePermissionGroups]
  );

  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
  const roleBuilderAccess = getFeatureAccess(providerUser, FeatureAccess.ROLE_BUILDER);
  const premiumPlanRequired = roleBuilderAccess.requiredPlanId;
  const { data: bookingPlansData } = useQuery(GET_PLANS, {
    variables: { product: "booking" },
    skip: !businessId,
  });
  const { data: bookingSubscriptionData } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId, productKey: "booking" },
    skip: !businessId,
    fetchPolicy: "network-only",
  });
  const bookingTierCode = getProductPlanTierCode(
    bookingSubscriptionData?.getBillingSubscription,
    bookingPlansData?.plans || []
  );
  const hasBookingPremiumAccess =
    hasStandaloneBookingAccess(providerUser) &&
    (bookingTierCode === "PREMIUM" || bookingTierCode === "ENTERPRISE");
  const hasPremiumAccess = roleBuilderAccess.hasAccess || hasBookingPremiumAccess;

  const currentLoginStaff = businessStaffs?.find(
    (staff) => Number(staff?.id) === Number(providerUser?.staffId)
  );
  const isAdmin = normalizeRoleName(currentLoginStaff?.role?.name) === ADMIN_ROLE;
  const roleReadOnly = Boolean(isDisabled || !isAdmin);
  const controlsDisabled = Boolean(roleReadOnly || !hasPremiumAccess);

  const roles = useMemo(
    () => toRolePayload(selectedBusiness?.roles || []),
    [selectedBusiness?.roles]
  );

  const [editingRole, setEditingRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [draftPermissions, setDraftPermissions] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [saving, setSaving] = useState(false);

  const [updateBusiness] = useMutation(UPDATE_BUSINESS_BASIC);

  const inUseCount = (role) => {
    const roleId = Number(role?.id || 0);
    const roleName = normalizeRoleName(role?.name);
    return businessStaffs.filter((staff) => {
      const staffRoleId = Number(staff?.role?.id || 0);
      const staffRoleName = normalizeRoleName(staff?.role?.name);
      return (roleId > 0 && staffRoleId === roleId) || staffRoleName === roleName;
    }).length;
  };

  const canDeleteRole = (role) =>
    normalizeRoleName(role?.name) !== ADMIN_ROLE && inUseCount(role) === 0;

  const validateDraft = () => {
    const name = String(draftName || "").trim();
    if (!name) return "Role name is required.";

    const normalizedName = normalizeRoleName(name);
    const duplicateRole = roles.find((role) => {
      if (normalizeRoleName(role?.name) !== normalizedName) return false;
      if (!editingRole) return true; // add mode: any same name is duplicate
      return Number(role?.id || 0) !== Number(editingRole?.id || 0); // edit mode: ignore self
    });
    if (duplicateRole) {
      return `Role "${name}" already exists. Edit the existing role instead of creating a new one.`;
    }

    if (normalizedName === NO_ACCESS_ROLE && draftPermissions.length > 0) {
      return "No Access role cannot include permissions.";
    }

    return "";
  };

  const openCreate = () => {
    if (!hasPremiumAccess) {
      setCheckPlanNeeded(true);
      return;
    }
    setEditingRole(null);
    setDialogOpen(true);
    setDraftName("");
    setDraftDescription("");
    setDraftPermissions([]);
    setErrorText("");
  };

  const openEdit = (role) => {
    if (controlsDisabled) return;
    setDialogOpen(true);
    setEditingRole(role);
    setDraftName(role?.name || "");
    setDraftDescription(role?.description || "");
    setDraftPermissions(toEffectiveRolePermissions(role, visiblePermissionKeys));
    setErrorText("");
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRole(null);
    setDraftName("");
    setDraftDescription("");
    setDraftPermissions([]);
    setErrorText("");
  };

  const togglePermission = (permission) => {
    if (controlsDisabled) return;
    setDraftPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((item) => item !== permission)
        : [...prev, permission]
    );
  };

  const persistRoles = async (nextRoles) => {
    if (controlsDisabled || !businessId || !businessBasic) return;
    setSaving(true);
    try {
      const { id, images, ...baseInput } = businessBasic;
      const response = await updateBusiness({
        variables: {
          input: {
            ...baseInput,
            id: Number(id),
            roles: nextRoles.map((role) => ({
              ...(role?.id ? { id: Number(role.id) } : {}),
              name: role.name,
              description: role.description,
              permissions: toPermissionArray(role.permissions),
            })),
          },
        },
      });

      const updatedBusiness = response?.data?.updateBusiness;
      if (!updatedBusiness) {
        throw new Error("Unable to save roles.");
      }

      const normalized = normalizeBusinessData(updatedBusiness)?.[0];
      if (normalized) dispatch(addBusiness(normalized));
      setErrorText("");
    } catch (error) {
      const rawMsg =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.message ||
        error?.message ||
        "Unable to save roles right now.";
      const msg = rawMsg.includes('Field "roles" is not defined by type "BusinessInput"')
        ? "Role builder backend is not updated yet. Deploy the server schema that supports BusinessInput.roles."
        : rawMsg;
      setErrorText(msg);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    const isEditingAdminRole =
      Boolean(editingRole) &&
      normalizeRoleName(editingRole?.name) === ADMIN_ROLE;
    const nextDraftRoleName = normalizeRoleName(draftName);
    if (isEditingAdminRole && nextDraftRoleName !== ADMIN_ROLE) {
      setErrorText("Admin role name cannot be changed.");
      return;
    }

    const isUpdate = Boolean(editingRole);
    const roleToSave = {
      ...(editingRole?.id ? { id: editingRole.id } : {}),
      name: String(draftName || "").trim(),
      description: String(draftDescription || "").trim(),
      permissions: [...draftPermissions].sort(),
    };

    const nextRoles = isUpdate
      ? roles.map((role) =>
          Number(role?.id || 0) === Number(editingRole?.id || 0) ? roleToSave : role
        )
      : [...roles, roleToSave];

    const validationError = validateDraft();
    if (validationError) {
      setErrorText(validationError);
      return;
    }

    try {
      await persistRoles(nextRoles);
      closeDialog();
    } catch (_err) {
      // keep dialog open and show error
    }
  };

  const handleDelete = async (role) => {
    if (saving) return;
    if (controlsDisabled || !canDeleteRole(role)) return;
    if (!window.confirm(`Delete role "${role.name}"?`)) return;

    const nextRoles = roles.filter((item) => {
      const roleId = Number(role?.id || 0);
      if (roleId > 0) return Number(item?.id || 0) !== roleId;
      return normalizeRoleName(item?.name) !== normalizeRoleName(role?.name);
    });

    try {
      await persistRoles(nextRoles);
    } catch (_err) {
      // errors shown by persistRoles
    }
  };

  return (
    <Box>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName="Custom role and permission builder"
          requiredPlanId={premiumPlanRequired}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Custom Role Builder
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create and maintain business-specific staff roles and permissions.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Shared permissions are always available. Care and booking permissions appear only
        when that product is active for this business.
      </Alert>

      {!hasPremiumAccess && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Premium plan required for custom role and permission builder.
        </Alert>
      )}

      {hasPremiumAccess && !isAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Admin role required to manage custom roles.
        </Alert>
      )}

      {!!errorText && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorText}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">Roles ({roles.length})</Typography>
        <Button variant="contained" onClick={openCreate} disabled={roleReadOnly || saving}>
          Add Role
        </Button>
      </Stack>

      <Grid container spacing={1.5}>
        {roles.map((role, idx) => {
          const usage = inUseCount(role);
          const isAdminRole = normalizeRoleName(role?.name) === ADMIN_ROLE;
          return (
            <Grid item xs={12} md={6} key={`${role?.id || idx}-${role?.name}`}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={700}>{role?.name}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => openEdit(role)} disabled={controlsDisabled}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={controlsDisabled || !canDeleteRole(role)}
                      onClick={() => handleDelete(role)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {role?.description || "No description"}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                  <Chip size="small" label={`${toEffectiveRolePermissions(role).length} permissions`} />
                  <Chip size="small" variant="outlined" label={`Used by ${usage} staff`} />
                  {isAdminRole && <Chip size="small" color="primary" label="Protected role" />}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingRole ? "Edit Role" : "Add Role"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {!!errorText && <Alert severity="error">{errorText}</Alert>}

            <TextField
              label="Role Name"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              disabled={
                Boolean(editingRole) &&
                normalizeRoleName(editingRole?.name) === ADMIN_ROLE
              }
              fullWidth
            />

            <TextField
              label="Description"
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              fullWidth
            />

            <Divider />
            <Typography variant="subtitle2">Permissions</Typography>

            <Grid container spacing={2}>
              {visiblePermissionGroups.map((group) => (
                <Grid item xs={12} md={6} key={group.title}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                      {group.title}
                    </Typography>
                    <FormGroup>
                      {group.items.map((perm) => (
                        <FormControlLabel
                          key={perm}
                          control={
                            <Checkbox
                              checked={draftPermissions.includes(perm)}
                              onChange={() => togglePermission(perm)}
                            />
                          }
                          label={perm}
                        />
                      ))}
                    </FormGroup>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || controlsDisabled}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
